<template>
  <b-container fluid>
    <div class="iq-card mt-3">
      <div class="iq-card-header d-flex justify-content-between">
        <div class="iq-header-title my-2">
          <h4 class="card-title">Asset List</h4>
        </div>
        <b-row>
          <b-col md="12">
            <div class="text-right">
              <button-options
                :access="'master_asset'"
                :hideAdd="false"
                :hideDetail="true"
                :isRefreshEnabled="true"
                :isRefreshed="isLoading"
                @onAddItemClicked="openAddForm"
                @onRefreshed="getData('Refreshed')"
              />
            </div>
          </b-col>
        </b-row>
      </div>
      <div class="iq-card-body">

        <!-- Controls: Search, Filter, Sort -->
        <b-row class="mb-3 align-items-end">
          <b-col md="4" sm="12" class="mb-2 mb-md-0">
            <div class="input-group input-group-sm">
              <div class="input-group-prepend">
                <span class="input-group-text">
                  <i v-if="isLoading && searchQuery" class="ri-loader-line"></i>
                  <i v-else class="ri-search-line"></i>
                </span>
              </div>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control"
                placeholder="Search by name..."
              />
              <div v-if="searchQuery" class="input-group-append">
                <button class="btn btn-outline-secondary" @click="searchQuery = ''; getData();">
                  <i class="ri-close-line"></i>
                </button>
              </div>
            </div>
          </b-col>

          <b-col md="5" sm="12" class="mb-2 mb-md-0">
            <div class="btn-group btn-group-sm w-100" role="group">
              <button
                v-for="opt in subscriptionFilterOptions"
                :key="opt.value"
                type="button"
                class="btn"
                :class="subscriptionFilter === opt.value ? `btn-${opt.variant}` : `btn-outline-${opt.variant}`"
                @click="subscriptionFilter = opt.value"
              >
                {{ opt.label }}
              </button>
            </div>
          </b-col>

          <b-col md="3" sm="12">
            <b-form-select v-model="sortBy" :options="sortOptions" size="sm" />
          </b-col>
        </b-row>

        <b-row v-if="isLoading">
          <b-col md="12">
            <div class="text-center">
              <b-spinner variant="primary" label="Spinning"></b-spinner>
            </div>
          </b-col>
        </b-row>

        <!-- CARD LIST UPDATED -->
        <b-row v-else-if="paginatedData.length === 0">
          <b-col md="12">
            <div class="text-center text-muted py-4">
              <i class="ri-inbox-line" style="font-size: 2rem;"></i>
              <p class="mt-2 mb-0">No assets found.</p>
            </div>
          </b-col>
        </b-row>

        <b-row v-else>
          <b-col lg="3" md="4" sm="12" v-for="(asset, index) in paginatedData" v-bind:key="`E-${index}`" class="my-2 d-flex align-items-stretch">
            <div class="iq-card shadow-sm w-100 border-0 rounded overflow-hidden d-flex flex-column mb-0">
              <!-- Image Section -->
              <div class="position-relative">
                <img
                  :src="asset.massImage !== '' ? '/api/' + asset.massImage : 'https://placehold.co/600x400?text=No+Image'"
                  :alt="asset.massName"
                  class="img-fluid w-100"
                  style="
                    height: 160px;
                    object-fit: cover;
                  "
                />
                <!-- Floating Asset Type Badge -->
                <div class="position-absolute" style="top: 8px; right: 8px;">
                   <b-badge variant="light" class="shadow-sm text-primary small py-1 px-2">{{ asset.massMaty ? asset.massMaty.matyName : '-' }}</b-badge>
                </div>
              </div>

              <!-- Body Section -->
              <div class="iq-card-body d-flex flex-column p-2">
                <h6 class="card-title font-weight-bold mb-1 text-truncate" :title="asset.massName" style="font-size: 1rem;">
                  {{ asset.massName }}
                </h6>

                <div class="mb-2">
                  <span class="text-muted small d-block mb-0" style="font-size: 0.75rem;">Installation</span>
                  <div class="text-dark font-weight-bold" style="font-size: 1.15rem; line-height: 1.2;">
                    {{ asset.massSystemType }}
                  </div>
                </div>

                <div class="mb-2">
                  <span class="text-muted small d-block mb-0" style="font-size: 0.75rem;">Subscription Exp.</span>
                  <b-badge
                    :variant="asset.massSubscriptionExpiredAt
                      ? (isSubscriptionExpired(asset.massSubscriptionExpiredAt)
                          ? 'danger'
                          : isExpiringSoon(asset.massSubscriptionExpiredAt)
                            ? 'warning'
                            : 'success')
                      : 'secondary'"
                    style="font-size: 0.72rem;"
                  >
                    {{ asset.massSubscriptionExpiredAt ? formatSubscriptionDate(asset.massSubscriptionExpiredAt) : 'Expiration Not Set' }}
                  </b-badge>
                </div>

                <!-- Spacer to push buttons to bottom -->
                <div class="mt-auto pt-2 border-top d-flex justify-content-between align-items-center">
                  <small class="text-muted" style="font-size: 0.75rem;">ID: #{{ asset.massId }}</small>
                  <div class="d-flex">
                    <!-- View Detail (Modal) -->
                    <b-button
                      variant="light"
                      size="sm"
                      class="rounded-circle mr-1 text-info p-0 d-flex align-items-center justify-content-center border-0"
                      style="width: 28px; height: 28px;"
                      @click="openDetailModal(asset)"
                      v-b-tooltip.hover
                      title="View Detail"
                    >
                      <i class="ri-eye-line"></i>
                    </b-button>

                    <!-- Manage Devices (New Button) -->
                    <b-button
                      variant="light"
                      size="sm"
                      class="rounded-circle mr-1 text-success p-0 d-flex align-items-center justify-content-center border-0"
                      style="width: 28px; height: 28px;"
                      @click="openDevices(asset.massId)"
                      v-b-tooltip.hover
                      title="Manage Devices"
                    >
                      <i class="ri-sensor-line"></i>
                    </b-button>

                    <!-- Edit -->
                    <b-button
                      variant="light"
                      size="sm"
                      class="rounded-circle mr-1 text-warning p-0 d-flex align-items-center justify-content-center border-0"
                      style="width: 28px; height: 28px;"
                      @click="openEditForm(asset.massId)"
                      v-b-tooltip.hover
                      title="Edit"
                    >
                      <i class="ri-edit-2-line"></i>
                    </b-button>

                    <!-- Delete -->
                    <b-button
                      variant="light"
                      size="sm"
                      class="rounded-circle text-danger p-0 d-flex align-items-center justify-content-center border-0"
                      style="width: 28px; height: 28px;"
                      @click="openDelete(asset)"
                      v-b-tooltip.hover
                      title="Delete"
                    >
                      <i class="ri-delete-bin-2-line"></i>
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
        <!-- END CARD LIST UPDATED -->

        <!-- Pagination -->
        <b-row v-if="!isLoading && filteredAndSortedData.length > clientPageSize" class="mt-3">
          <b-col md="12" class="d-flex justify-content-between align-items-center flex-wrap">
            <small class="text-muted mb-2 mb-md-0">
              Showing {{ (clientPage - 1) * clientPageSize + 1 }}–{{ Math.min(clientPage * clientPageSize, filteredAndSortedData.length) }}
              of {{ filteredAndSortedData.length }} assets
            </small>
            <b-pagination
              v-model="clientPage"
              :total-rows="filteredAndSortedData.length"
              :per-page="clientPageSize"
              size="sm"
              class="mb-0"
            />
          </b-col>
        </b-row>

      </div>

      <!-- create/update modal -->
      <b-modal
        size="lg"
        hide-footer
        no-close-on-esc
        no-close-on-backdrop
        v-model="displayForm"
      >
        <asset-form
          :data="currentAsset"
          :assetTypes="assetType"
          @onSuccess="onAddOrUpdateSuccess"
          @onCancel="onCancel"
        />
      </b-modal>
      <!-- ./create/update modal -->

      <!-- NEW: Detail Modal (Comprehensive & Compact) -->
      <b-modal
        size="lg"
        title="Asset Detail"
        hide-footer
        v-model="displayDetail"
        body-class="p-0"
      >
        <div v-if="selectedAsset && selectedAsset.massId">
            <!-- Header Section with Image and Main Info -->
            <div class="bg-light p-3 d-flex align-items-start border-bottom">
                 <img
                  :src="selectedAsset.massImage ? '/api/' + selectedAsset.massImage : 'https://placehold.co/120x120?text=No+Image'"
                  class="rounded mr-3 shadow-sm"
                  style="width: 100px; height: 100px; object-fit: cover;"
                />
                <div class="flex-grow-1">
                    <h5 class="font-weight-bold mb-1">{{ selectedAsset.massName }}</h5>
                    <div class="mb-2">
                         <b-badge variant="primary" class="mr-1">{{ selectedAsset.massSystemType }}</b-badge>
                         <b-badge variant="info">{{ selectedAsset.massMaty ? selectedAsset.massMaty.matyName : 'Unknown Type' }}</b-badge>
                    </div>
                    <div class="text-muted small">
                        <i class="ri-calendar-line mr-1"></i> Installation Date:
                        <span class="text-dark font-weight-bold">{{ selectedAsset.massInstallationDate || '-' }}</span>
                    </div>
                    <div class="text-muted small mt-1">
                        <i class="ri-time-line mr-1"></i> Subscription Expired At:
                        <span class="text-dark font-weight-bold">{{
                          formatSubscriptionDate(selectedAsset.massSubscriptionExpiredAt) }}</span>
                    </div>
                </div>
            </div>

            <div class="p-3">
                <b-row>
                    <!-- Left Column: Contact Person (PIC) -->
                    <b-col md="6" class="border-right border-md-0">
                        <h6 class="text-uppercase text-secondary mb-3 small font-weight-bold border-bottom pb-1">
                            <i class="ri-user-star-line mr-1"></i> Contact Person (PIC)
                        </h6>
                        <div class="detail-row mb-2">
                            <span class="text-muted small">Name</span>
                            <span class="font-weight-bold d-block">{{ selectedAsset.massPicName || '-' }}</span>
                        </div>
                        <div class="detail-row mb-2">
                            <span class="text-muted small">Contact / Phone</span>
                            <span class="font-weight-bold d-block">{{ selectedAsset.massPicContact || '-' }}</span>
                        </div>
                        <div class="detail-row mb-2">
                            <span class="text-muted small">Email</span>
                            <span class="font-weight-bold d-block">{{ selectedAsset.massPicEmail || '-' }}</span>
                        </div>
                         <div class="detail-row mb-2">
                            <span class="text-muted small">Email CC</span>
                            <span class="font-weight-bold d-block">{{ selectedAsset.massPicEmailCc || '-' }}</span>
                        </div>
                    </b-col>

                    <!-- Right Column: Connectivity & Hardware -->
                    <b-col md="6">
                        <h6 class="text-uppercase text-secondary mb-3 small font-weight-bold border-bottom pb-1">
                             <i class="ri-wifi-line mr-1"></i> Connectivity & Hardware
                        </h6>
                        <b-row>
                            <b-col cols="6" class="mb-2">
                                <span class="text-muted small d-block">GSM Number</span>
                                <span class="font-weight-bold">{{ selectedAsset.massGsmNo || '-' }}</span>
                            </b-col>
                            <b-col cols="6" class="mb-2">
                                <span class="text-muted small d-block">IMEI</span>
                                <span class="font-weight-bold">{{ selectedAsset.massImei || '-' }}</span>
                            </b-col>
                            <b-col cols="6" class="mb-2">
                                <span class="text-muted small d-block">Telco Operator</span>
                                <span class="font-weight-bold">{{ selectedAsset.massTelcoOperator || '-' }}</span>
                            </b-col>
                             <b-col cols="6" class="mb-2">
                                <span class="text-muted small d-block">Satellite ID</span>
                                <span class="font-weight-bold">{{ selectedAsset.massSateliteId || '-' }}</span>
                            </b-col>
                             <b-col cols="6" class="mb-2">
                                <span class="text-muted small d-block">Sat. Operator</span>
                                <span class="font-weight-bold">{{ selectedAsset.massSateliteOperator || '-' }}</span>
                            </b-col>
                             <b-col cols="6" class="mb-2">
                                <span class="text-muted small d-block">Sat. Device ID</span>
                                <span class="font-weight-bold">{{ selectedAsset.massSatDeviceId || '-' }}</span>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
            </div>

            <!-- New Section: Connected Devices -->
            <div class="p-3 border-top bg-white">
                <h6 class="text-uppercase text-secondary mb-3 small font-weight-bold">
                    <i class="ri-sensor-line mr-1"></i> Connected Devices
                </h6>
                <div v-if="isLoadingDevices" class="text-center py-3">
                    <b-spinner small variant="primary" /><span class="ml-2 small text-muted">Loading devices...</span>
                </div>
                <div v-else-if="selectedDevices && selectedDevices.length > 0" class="table-responsive">
                    <table class="table table-sm table-borderless table-striped small mb-0">
                        <thead class="bg-light">
                            <tr>
                                <th class="pl-3">Name</th>
                                <th>Type</th>
                                <th>Unique ID</th>
                                <th>Status</th>
                                <th class="text-right pr-3">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="dev in selectedDevices" :key="dev.devcId">
                                <td class="font-weight-bold pl-3">{{ dev.devcName }}</td>
                                <td>
                                  <b-badge variant="soft-info" class="text-uppercase">{{ dev.devcType }}</b-badge>
                                </td>
                                <td class="text-muted font-italic">{{ dev.devcUniqueId }}</td>
                                <td>
                                    <b-badge :variant="dev.devcStatus === 1 ? 'success' : 'secondary'">
                                        {{ dev.devcStatus === 1 ? 'Active' : 'Inactive' }}
                                    </b-badge>
                                </td>
                                <td class="text-right pr-3">
                                   <b-button
                                      variant="light"
                                      size="sm"
                                      class="rounded-circle text-primary p-0 d-flex align-items-center justify-content-center border-0"
                                      style="width: 24px; height: 24px;"
                                      @click="openDeviceDetail(dev)"
                                      v-b-tooltip.hover
                                      title="View Device Data"
                                   >
                                      <i class="ri-eye-line"></i>
                                   </b-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else class="text-muted small text-center py-2 bg-light rounded">
                    No devices linked to this asset.
                </div>

            </div>

            <!-- Footer: Meta Data -->
            <div class="bg-lighter p-2 px-3 border-top d-flex justify-content-between small text-muted">
                <div>
                    Created: <b>{{ selectedAsset.massCreatedUserId }}</b> ({{ selectedAsset.massCreatedTime }})
                </div>
                 <div>
                    Updated: <b>{{ selectedAsset.massUpdatedUserId }}</b> ({{ selectedAsset.massUpdatedTime }})
                </div>
            </div>
        </div>
      </b-modal>
      <!-- ./detail modal -->

      <!-- delete modal -->
      <b-modal
        size="lg"
        hide-footer
        no-close-on-esc
        no-close-on-backdrop
        v-model="displayDeletion"
      >
        <DeleteItem
          :isSubmit="isSubmit"
          @onCancelDeletion="displayDeletion = false"
          @onConfirmDeletion="onConfirmDeletion"
        />
      </b-modal>
      <!-- ./delete modal -->
    </div>
  </b-container>
</template>

<script>
import AssetForm from '@/components/asset/Form.vue';
// import AssetList from '@/components/asset/List.vue';
import AssetSvc from '@/services/AssetSvc';
import AssetTypeSvc from '@/services/AssetTypeSvc';
import ButtonOptions from '@/components/directives/button-options';
import DeleteItem from '@/components/directives/delete-item';
import DeviceSvc from '@/services/DeviceSvc';

import requestBuilder from '@/util/master/request.builder';
import { DISPLAY_DATE } from '@/util/apiDatetime';
import {
  subscriptionEndsAt, isSubscriptionExpired, daysUntilExpiry,
} from '@/util/subscription';

// Sama dengan WARN_DAYS di store/notification — dua-duanya ambang "segera habis".
const EXPIRING_SOON_DAYS = 30;

export default {
  name: 'Asset',
  data() {
    return {
      currentAsset: {},
      selectedAsset: {},
      selectedDevices: [],
      isLoadingDevices: false,
      assetType: [],
      data: [],
      displayForm: false,
      displayDetail: false,
      displayDeletion: false,
      filter: {
        massType: null,
      },
      isLoading: false,
      isSubmit: false,
      pagination: {
        current: 1,
        pageSize: 10,
        totalSize: 0,
      },
      fields: {},
      searchQuery: '',
      subscriptionFilter: 'all',
      sortBy: 'name_asc',
      clientPage: 1,
      clientPageSize: 12,
      subscriptionFilterOptions: [
        { value: 'all', label: 'All', variant: 'secondary' },
        { value: 'active', label: 'Active', variant: 'success' },
        { value: 'expired', label: 'Expired', variant: 'danger' },
        { value: 'not_set', label: 'Not Set', variant: 'secondary' },
      ],
      sortOptions: [
        { value: 'name_asc', text: 'Name A–Z' },
        { value: 'name_desc', text: 'Name Z–A' },
        { value: 'expiry_asc', text: 'Expiry: Earliest' },
        { value: 'expiry_desc', text: 'Expiry: Latest' },
        { value: 'updated_desc', text: 'Recently Updated' },
      ],
    };
  },
  computed: {
    filteredAndSortedData() {
      let result = [...this.data];

      if (this.subscriptionFilter === 'active') {
        result = result.filter((a) => a.massSubscriptionExpiredAt && !this.isSubscriptionExpired(a.massSubscriptionExpiredAt));
      } else if (this.subscriptionFilter === 'expired') {
        result = result.filter((a) => a.massSubscriptionExpiredAt && this.isSubscriptionExpired(a.massSubscriptionExpiredAt));
      } else if (this.subscriptionFilter === 'not_set') {
        result = result.filter((a) => !a.massSubscriptionExpiredAt);
      }

      return result;
    },
    paginatedData() {
      const start = (this.clientPage - 1) * this.clientPageSize;
      return this.filteredAndSortedData.slice(start, start + this.clientPageSize);
    },
  },
  watch: {
    searchQuery() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.clientPage = 1;
        this.getData();
      }, 400);
    },
    subscriptionFilter() { this.clientPage = 1; },
    sortBy() {
      this.clientPage = 1;
      this.getData();
    },
  },
  components: {
    DeleteItem,
    ButtonOptions,
    AssetForm,
    // AssetList,
  },
  methods: {
    isSubscriptionExpired,
    // Habis hari ini masih dihitung aktif, jadi ambangnya sisa hari, bukan instan.
    isExpiringSoon(dateStr) {
      const days = daysUntilExpiry(dateStr);
      return days !== null && days >= 0 && days <= EXPIRING_SOON_DAYS;
    },
    formatSubscriptionDate(dateStr) {
      const end = subscriptionEndsAt(dateStr);
      return end ? end.format(DISPLAY_DATE) : '-';
    },
    async deleteData(notification, massId) {
      this.isSubmit = true;
      try {
        const resp = await AssetSvc.deleteAsset(massId);
        if (resp.data.status.error === 0) {
          this.$toast.success(notification);
          this.displayDeletion = false;
          this.pagination.current = 1;
          this.getData();
        } else {
          this.$toast.error(resp.data.status.message);
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.isSubmit = false;
      }
    },
    async getData() {
      try {
        this.isLoading = true;
        const filterParams = { ...this.filter };
        if (this.searchQuery.trim()) {
          filterParams.massName = this.searchQuery.trim();
        }

        const sortMap = {
          name_asc: { massName: 'ASC' },
          name_desc: { massName: 'DESC' },
          expiry_asc: { massSubscriptionExpiredAt: 'ASC' },
          expiry_desc: { massSubscriptionExpiredAt: 'DESC' },
          updated_desc: { massUpdatedTime: 'DESC' },
        };
        const orderParams = {};
        const sortEntry = sortMap[this.sortBy];
        if (sortEntry) {
          Object.entries(sortEntry).forEach(([field, dir]) => {
            orderParams[`orders[${field}]`] = dir;
          });
        }

        const assetResponse = await AssetSvc.getAssets({
          params: {
            ...requestBuilder.buildFilters({ current: 1, pageSize: 1000 }, filterParams),
            ...orderParams,
          },
        });

        const mappedFields = Object.values(assetResponse.data.fields || {}).filter(
          (field) => {
            const isHide = [
              'massId',
              'massCompId',
              'massDataId',
              'massType',
              'massFileId',
              'massSatDeviceId',
              'massCreatedUserId',
              'massCreatedTime',
              'massUpdatedUserId',
              'massUpdatedTime',
            ];
            return !isHide.includes(field.Field);
          },
        ).map((field) => {
          if (field.Field === 'massMatyId') {
            return {
              ...field,
              Label: 'Asset Type',
            };
          }
          return field;
        });

        this.data = assetResponse.data.data || [];
        this.fields = mappedFields;
        this.pagination.totalSize = assetResponse.data.pagination?.totalData || 0;
      } catch (e) {
        console.error('getData: ', e);
      } finally {
        this.isLoading = false;
      }
    },
    onAddOrUpdateSuccess() {
      this.getData();
      this.displayForm = false;
    },
    onCancel() {
      this.currentAsset = {};
      this.displayForm = false;
    },
    onConfirmDeletion() {
      this.deleteData('Deleted complete', this.selected.massId);
    },
    openAddForm() {
      this.currentAsset = {};
      this.displayForm = true;
    },
    openDelete(data) {
      this.displayDeletion = true;
      this.selected = data;
    },
    // New method for opening the detail modal
    async openDetailModal(asset) {
      this.selectedAsset = asset;
      this.selectedDevices = [];
      this.isLoadingDevices = true;
      this.displayDetail = true;

      try {
        const param = {
          findField: 'devcMassId',
          findValue: asset.massId,
          max: 100,
        };
        const devices = await DeviceSvc.getDevicesByAssetId(param);
        if (devices && devices.data && devices.data.data) {
          this.selectedDevices = devices.data.data;
        }
      } catch (e) {
        console.error('Failed to fetch devices', e);
      } finally {
        this.isLoadingDevices = false;
      }
    },
    openDetail(massId) {
      this.$router.push({ name: 'AssetDetail', params: { id: massId } });
    },
    openDeviceDetail(data) {
      const route = this.$router.resolve({ name: 'DeviceDetail', params: { id: data.devcId } });
      window.open(route.href, '_blank');
    },
    openEditForm(massId) {
      const foundAsset = this._.find(this.data, (v) => v.massId === massId);
      if (foundAsset) {
        const { massMaty, ...currentAsset } = foundAsset;
        this.currentAsset = currentAsset;
      }

      this.displayForm = true;
    },
    openDevices(massId) {
      this.$router.push({ name: 'AssetDevice', params: { id: massId } });
    },
  },
  async beforeMount() {
    const { subscriptionFilter } = this.$route.query;
    if (subscriptionFilter && this.subscriptionFilterOptions.some((o) => o.value === subscriptionFilter)) {
      this.subscriptionFilter = subscriptionFilter;
    }

    this.getData();

    const assetTypesResponse = await AssetTypeSvc.getAssetType({ pageSize: 100 });
    // role tanpa izin asset type dibales 412 tanpa data
    const assetTypesData = assetTypesResponse?.data || [];

    this.assetType = assetTypesData.map((assetType) => ({
      value: assetType.matyId,
      text: assetType.matyName,
    }));
  },
};
</script>
